import React from 'react';
import {
  ApplicationIcon,
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import type { User } from '../../../../services';
import type { EntitlementRow } from '../IncludedEntitlements';

import {
  getAccessStatusClassName,
  getScoreChipClassName,
  getScoreChipColor,
  getScoreIcon,
} from './utilityFunctions';

// Helper function to convert boolean accessStatus to string
const getAccessStatusText = (accessStatus?: boolean): string => {
  if (accessStatus === true) {
    return 'Present';
  }

  if (accessStatus === false) {
    return 'Absent';
  }

  return '-'; // default to dash to help identify any data issues
};

/**
 * Creates the center content for parent rows (entitlement rows)
 */
export const createParentCenterContent = (
  entitlement: EntitlementRow,
  onRowClick?: (data: EntitlementRow) => void
) => (
  // click handler added due to increased z-index which blocks the sidepanel from opening
  <div
    className='IncludedEntitlements-centerContent'
    onClick={() => {
      onRowClick?.(entitlement);
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick?.(entitlement);
      }
    }}
    role='button'
    tabIndex={0}>
    <Typography
      kind='body1-bold'
      lineLimit={1}
      tooltipConfigs={{
        text: entitlement.evDisplayName ?? entitlement.entitlementValueName,
        placement: 'top',
        maxWidth: '10rem',
      }}
      className='IncludedEntitlements-endpointName'>
      {entitlement.evDisplayName ?? entitlement.entitlementValueName}
    </Typography>
    <Typography
      kind='body2'
      lineLimit={1}
      tooltipConfigs={{
        text: entitlement.evDescription ?? '-',
        placement: 'top',
        maxWidth: '10rem',
      }}
      className='IncludedEntitlements-endpointDescription'>
      {entitlement.evDescription}
    </Typography>
  </div>
);

/**
 * Creates the trailing content for parent rows (entitlement rows)
 */
export const createParentTrailingContent = (
  entitlement: EntitlementRow,
  totalVisibleEntRows: number,
  onDeleteClick: (entitlement: EntitlementRow) => void
) => {
  const scoreIcon = getScoreIcon(entitlement.trustScore);
  const chipColor = getScoreChipColor(entitlement.trustScore);
  const chipClassName = getScoreChipClassName(entitlement.trustScore);
  const isLastEntitlement = totalVisibleEntRows === 1;
  const usersTooltipText = `${entitlement.totalUserCount} users meet this rule's criteria. ${entitlement.existingUserCount} currently have access to the entitlement.`;

  return (
    <Box className='IncludedEntitlements-trailingContent'>
      <ApplicationIcon
        logo={entitlement.logo}
        size='medium'
        className='IncludedEntitlements-systemIcon'
      />

      <Chip
        size='small'
        label={entitlement.trustScore.toString()}
        kind='outlined'
        color={chipColor}
        leftIcon={<Icon kind={scoreIcon} />}
        className={cx('Rules-IncludedEntitlements-scoreChip', chipClassName)}
      />

      <Box className='IncludedEntitlements-userMetric'>
        <Icon kind='User' size='small' />
        <Tooltip placement='top' text={usersTooltipText}>
          <Typography kind='body2' className='IncludedEntitlements-userCount'>
            {entitlement.existingUserCount}/{entitlement.totalUserCount}
          </Typography>
        </Tooltip>
      </Box>

      <ButtonIcon
        isDisabled={isLastEntitlement}
        size='medium'
        kind='ghost'
        icon={<Icon kind='Delete' />}
        ariaLabel={
          isLastEntitlement
            ? 'Cannot delete the last entitlement'
            : 'Delete entitlement'
        }
        className='IncludedEntitlements-deleteButton'
        onClick={
          isLastEntitlement ? undefined : () => onDeleteClick(entitlement)
        }
      />
    </Box>
  );
};

/**
 * Creates the center content for user rows
 */
export const createUserCenterContent = (
  user: User,
  onRowClick?: (user: User) => void
) => (
  <div
    className='IncludedEntitlements-userRowContent'
    onClick={() => {
      onRowClick?.(user);
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick?.(user);
      }
    }}
    role='button'
    tabIndex={0}>
    {/* User info section */}
    <Box className='IncludedEntitlements-userColumn'>
      <Box className='IncludedEntitlements-userInfo'>
        <Icon
          kind='User'
          size='medium'
          className='IncludedEntitlements-userIcon'
        />
        <Box className='IncludedEntitlements-userDetails'>
          <Typography kind='body3' className='IncludedEntitlements-userLabel'>
            User
          </Typography>
          <Typography
            kind='body2-bold'
            lineLimit={1}
            tooltipConfigs={{
              text: user.username,
              placement: 'top',
              maxWidth: '10rem',
            }}
            className='IncludedEntitlements-userName'>
            {/* Show available name parts or dash if both are null/undefined */}
            {[user.firstname, user.lastname].filter(Boolean).join(' ') || '-'}
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* Username column */}
    <Box className='IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: 'Username',
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='IncludedEntitlements-columnLabel'>
        Username
      </Typography>
      <Typography
        kind='body2'
        className='IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.username,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {user.username ?? '-'}
      </Typography>
    </Box>

    {/* Job Title column */}
    <Box className='IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: 'Job Title',
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='IncludedEntitlements-columnLabel'>
        Job Title
      </Typography>
      <Typography
        kind='body2'
        className='IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.title,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {user.title || '-'}
      </Typography>
    </Box>

    {/* Department column */}
    <Box className='IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: 'Department',
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='IncludedEntitlements-columnLabel'>
        Department
      </Typography>
      <Typography
        kind='body2'
        className='IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.departmentname,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {user.departmentname ?? '-'}
      </Typography>
    </Box>

    {/* Manager column */}
    <Box className='IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: 'Manager',
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='IncludedEntitlements-columnLabel'>
        Manager
      </Typography>
      <Typography
        kind='body2'
        className='IncludedEntitlements-columnValue'
        lineLimit={1}
        tooltipConfigs={{
          text: user.manager,
          placement: 'top',
          maxWidth: '10rem',
        }}>
        {user.manager || '-'}
      </Typography>
    </Box>

    {/* Access Status column */}
    <Box className='IncludedEntitlements-userDataColumn'>
      <Typography
        kind='body3'
        lineLimit={1}
        tooltipConfigs={{
          text: 'Access Status',
          placement: 'top',
          maxWidth: '10rem',
        }}
        className='IncludedEntitlements-columnLabel'>
        Access Status
      </Typography>
      <Typography
        kind='body2'
        lineLimit={1}
        tooltipConfigs={{
          text: getAccessStatusText(user.accessStatus),
          placement: 'top',
          maxWidth: '10rem',
        }}
        className={getAccessStatusClassName(
          getAccessStatusText(user.accessStatus)
        )}>
        {getAccessStatusText(user.accessStatus)}
      </Typography>
    </Box>
  </div>
);

/**
 * Creates the loading state content for child rows
 */
export const createLoadingStateContent = () => (
  <Box className='IncludedEntitlements-loadingState'>
    <Typography kind='body2' color='--color-foreground-neutral-subtle'>
      <Loader kind='dots' format='inline' color='brand' />
    </Typography>
  </Box>
);

/**
 * Creates the placeholder content for child rows when no users are loaded
 */
export const createPlaceholderContent = () => (
  <Box className='IncludedEntitlements--noUsersFound'>
    <Icon
      kind='AlertInfo'
      size='medium'
      color='--color-foreground-info-medium'
    />
    <Typography kind='body2' color='--color-foreground-neutral-bold'>
      Looks like there aren&apos;t any users to display.
    </Typography>
  </Box>
);

/**
 * Creates the leading content for parent rows (entitlement groups)
 */
export const createParentLeadingContent = () => (
  <Box className='IncludedEntitlements-leadingContent'>
    <Icon kind='Entitlement' size='large' />
  </Box>
);
